import { AsyncStatus, MetaConversation } from 'lib-shared-legacy-types';
import { User } from 'lib-shared-types-api';
import { handleActions } from 'redux-actions';

import { sortEmailIntegrationsList } from 'utils/emailIntegration';

import { composeEmailActions } from '../actions/composeEmailActions';

export type ComposeEmailState = {
  meta: {
    send: AsyncStatus;
    findUser: AsyncStatus;
    getAllFrom: AsyncStatus;
    getPresignedUrl: AsyncStatus;
    getDrafts: AsyncStatus;
    saveDraft: AsyncStatus;
    removeDraft: AsyncStatus;
  };
  ui: {
    isModalOpen: boolean;
  };
  emailDrafts: Array<any>;
  fromEmails: FromEmail[];
  users: Array<User>;
  initialValues: any;
  lastUsedEmail: string | null;
  parentConversation?: MetaConversation | null;
};

export type FromEmail = {
  organization_id: string;
  queue_id: string | null;
  email_address: string;
  sender_name: string | null;
  spf_domain: string | null;
  spf_sender_email: string | null;
  signature_template: string | null;
  created_at: string;
  updated_at: string | null;
};

const initialState: ComposeEmailState = {
  meta: {
    send: 'succeeded',
    findUser: 'succeeded',
    getAllFrom: 'succeeded',
    getPresignedUrl: 'succeeded',
    getDrafts: 'succeeded',
    saveDraft: 'succeeded',
    removeDraft: 'succeeded',
  },
  ui: {
    isModalOpen: false,
  },
  emailDrafts: [],
  fromEmails: [],
  users: [],
  initialValues: null,
  lastUsedEmail: null,
  parentConversation: null,
};

export const {
  composeEmailSend,
  composeEmailSendSucceeded,
  composeEmailSendFailed,
  composeEmailFindUser,
  composeEmailFindUserSucceeded,
  composeEmailFindUserFailed,
  composeEmailGetAllFrom,
  composeEmailGetAllFromSucceeded,
  composeEmailGetAllFromFailed,
  composeEmailGetDrafts,
  composeEmailGetDraftsSucceeded,
  composeEmailGetDraftsFailed,
  composeEmailSaveDraft,
  composeEmailSaveDraftSucceeded,
  composeEmailSaveDraftFailed,
  composeEmailRemoveDraft,
  composeEmailRemoveDraftSucceeded,
  composeEmailRemoveDraftFailed,
  composeEmailOpenModal,
  composeEmailCloseModal,
  composeEmailSaveParentConv,
  composeEmailChangeSatisfactionLanguage,
  composeEmailCancelSatisfactionLanguage,
} = composeEmailActions;

const composeEmail = handleActions<ComposeEmailState, any>(
  {
    [composeEmailCloseModal.toString()]: (
      state: ComposeEmailState,
    ): ComposeEmailState => {
      return {
        ...state,
        ui: { ...state.ui, isModalOpen: false },
        initialValues: null,
        parentConversation: null,
      };
    },

    [composeEmailOpenModal.toString()]: (
      state: ComposeEmailState,
      action: any,
    ): ComposeEmailState => {
      let initialValues = null;
      if (
        action.payload !== undefined &&
        action.payload.initialValues !== undefined
      ) {
        initialValues = Object.assign({}, action.payload.initialValues);
      }
      return {
        ...state,
        ui: { ...state.ui, isModalOpen: true },
        initialValues,
      };
    },
    [composeEmailSaveParentConv.toString()]: (
      state: ComposeEmailState,
      action: any,
    ): ComposeEmailState => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const { payload: { parentConversation } = {} } = action;

      if (parentConversation) {
        return {
          ...state,
          parentConversation,
        };
      }
      return state;
    },

    [composeEmailGetDrafts.toString()]: (
      state: ComposeEmailState,
    ): ComposeEmailState => {
      return { ...state, meta: { ...state.meta, getDrafts: 'loading' } };
    },
    [composeEmailGetDraftsSucceeded.toString()]: (
      state: ComposeEmailState,
      action: any,
    ): ComposeEmailState => {
      let drafts = [];
      if (action.payload && action.payload.drafts) {
        drafts = action.payload.drafts;
        drafts.sort((a: any, b: any) => {
          return b.timestamp - a.timestamp;
        });
      }
      return {
        ...state,
        meta: { ...state.meta, getDrafts: 'succeeded' },
        emailDrafts: drafts,
      };
    },

    [composeEmailGetDraftsFailed.toString()]: (
      state: ComposeEmailState,
    ): ComposeEmailState => {
      return { ...state, meta: { ...state.meta, getDrafts: 'failed' } };
    },

    [composeEmailSaveDraft.toString()]: (
      state: ComposeEmailState,
    ): ComposeEmailState => {
      return { ...state, meta: { ...state.meta, saveDraft: 'loading' } };
    },

    [composeEmailSaveDraftSucceeded.toString()]: (
      state: ComposeEmailState,
      action: any,
    ): ComposeEmailState => {
      const drafts = state.emailDrafts.slice();
      if (action.payload && action.payload.drafts) {
        drafts.push(action.payload.drafts);
      }
      return {
        ...state,
        meta: { ...state.meta, saveDraft: 'succeeded' },
        emailDrafts: drafts,
      };
    },

    [composeEmailSaveDraftFailed.toString()]: (
      state: ComposeEmailState,
    ): ComposeEmailState => {
      return { ...state, meta: { ...state.meta, saveDraft: 'failed' } };
    },

    [composeEmailRemoveDraft.toString()]: (
      state: ComposeEmailState,
    ): ComposeEmailState => {
      return { ...state, meta: { ...state.meta, removeDraft: 'loading' } };
    },

    [composeEmailRemoveDraftSucceeded.toString()]: (
      state: ComposeEmailState,
      action: any,
    ): ComposeEmailState => {
      let emailDrafts = [];
      if (action.payload && action.payload.drafts) {
        emailDrafts = action.payload.drafts;
      }
      return {
        ...state,
        meta: { ...state.meta, removeDraft: 'succeeded' },
        emailDrafts: emailDrafts,
      };
    },

    [composeEmailRemoveDraftFailed.toString()]: (
      state: ComposeEmailState,
    ): ComposeEmailState => {
      return { ...state, meta: { ...state.meta, removeDraft: 'failed' } };
    },

    [composeEmailSend.toString()]: (
      state: ComposeEmailState,
    ): ComposeEmailState => {
      return { ...state, meta: { ...state.meta, send: 'loading' } };
    },

    [composeEmailSendSucceeded.toString()]: (
      state: ComposeEmailState,
    ): ComposeEmailState => {
      return { ...state, meta: { ...state.meta, send: 'succeeded' } };
    },

    [composeEmailSendFailed.toString()]: (
      state: ComposeEmailState,
    ): ComposeEmailState => {
      return { ...state, meta: { ...state.meta, send: 'failed' } };
    },

    [composeEmailFindUser.toString()]: (
      state: ComposeEmailState,
    ): ComposeEmailState => {
      return { ...state, meta: { ...state.meta, findUser: 'loading' } };
    },

    [composeEmailFindUserSucceeded.toString()]: (
      state: ComposeEmailState,
      action: any,
    ): ComposeEmailState => {
      const hits =
        action.payload.data !== undefined &&
        action.payload.data[0] !== undefined &&
        Array.isArray(action.payload.data[0].hits) === true
          ? action.payload.data[0].hits
          : [];
      const users = hits.filter(
        (user) =>
          user.email !== undefined &&
          user.email !== null &&
          typeof user.email === 'string',
      );
      return {
        ...state,
        meta: { ...state.meta, findUser: 'succeeded' },
        users: users,
      };
    },

    [composeEmailFindUserFailed.toString()]: (
      state: ComposeEmailState,
    ): ComposeEmailState => {
      return { ...state, meta: { ...state.meta, findUser: 'failed' } };
    },

    [composeEmailGetAllFrom.toString()]: (
      state: ComposeEmailState,
    ): ComposeEmailState => {
      return { ...state, meta: { ...state.meta, getAllFrom: 'loading' } };
    },

    [composeEmailGetAllFromSucceeded.toString()]: (
      state: ComposeEmailState,
      action: any,
    ): ComposeEmailState => {
      const data =
        action.payload.data !== undefined &&
        Array.isArray(action.payload.data) === true
          ? action.payload.data
          : [];

      return {
        ...state,
        meta: { ...state.meta, getAllFrom: 'succeeded' },
        fromEmails: sortEmailIntegrationsList(data),
      };
    },

    [composeEmailGetAllFromFailed.toString()]: (
      state: ComposeEmailState,
    ): ComposeEmailState => {
      return { ...state, meta: { ...state.meta, getAllFrom: 'failed' } };
    },
  },
  initialState,
);

export default composeEmail;
